import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import PageTitle from '../../layouts/PageTitle';
import { useStaticCollections } from '../../../context/StaticCollections';
import { useAdminCollections } from '../../../context/AdminCollections';
import { useLoggedInUserContextProvider } from '../../../context/LoggedInUserContext';
import { addCmseCommitteeToSectionalCommitteeArray } from '../../../utils/addCmseCommitteeToSectionalCommitteeArray';
import { getNominationRoundYear } from '../../../utils/getNominationRoundYear';

import { useUserPermissions } from '../../hooks/useUserPermissions';
import FilteringTable from '../Table/FilteringTable/FilteringTable';
import ColumnFilter from '../Table/FilteringTable/ColumnFilter';

import { tableColumnNamesScoring } from '../../../utils/tableColumnNamesScoring';

import { getScoringTableData } from '../../../utils/getScoringTableData';
import { filteringTableGetTableColumns } from '../../../utils/filteringTableGetTableColumns';
import { filterObserverCommittees } from '../../../utils/filterObserverCommittees';

const ScoringCompletionStatus = ({ value }) => {
  const getStatusBadge = (status) => {
    switch (status) {
      case 'Complete':
        return <span className="badge bg-success">{status}</span>;
      case 'In Progress':
        return <span className="badge bg-warning text-dark">{status}</span>;
      case 'Not Started':
        return <span className="badge bg-secondary">{status}</span>;
      default:
        return <span className="badge bg-light text-dark">{status}</span>;
    }
  };

  return getStatusBadge(value);
};

ScoringCompletionStatus.propTypes = {
  value: PropTypes.string.isRequired,
};

const ProgressBar = ({ value }) => {
  const percentage = parseInt(value.replace('%', ''), 10);

  const getProgressBarClass = (percent) => {
    if (percent === 100) return 'progress-bar bg-success';
    if (percent >= 50) return 'progress-bar bg-warning';
    return 'progress-bar bg-danger';
  };

  return (
    <div className="d-flex align-items-center">
      <div className="progress me-2" style={{ width: '100px', height: '20px' }}>
        <div
          className={getProgressBarClass(percentage)}
          role="progressbar"
          style={{ width: `${percentage}%` }}
          aria-valuenow={percentage}
          aria-valuemin="0"
          aria-valuemax="100"
          aria-label={`Progress: ${percentage}%`}
        />
      </div>
      <small className="text-muted">{value}</small>
    </div>
  );
};

ProgressBar.propTypes = {
  value: PropTypes.string.isRequired,
};

const CommitteeCardHeader = ({ committeeName }) => (
  <div className="card-header text-center">
    <h4 className="card-title mb-2">
      <strong>{committeeName}</strong>
    </h4>
    <h6 className="text-muted">
      {getNominationRoundYear(new Date())} ROUND - Scoring Completion Report
    </h6>
  </div>
);

CommitteeCardHeader.propTypes = {
  committeeName: PropTypes.string.isRequired,
};

const renderCompletionStatus = (info) => {
  const value = info.getValue?.() ?? info.row.original.completionStatus;
  return <ScoringCompletionStatus value={value} />;
};

const renderProgressBar = (info) => {
  const value = info.getValue?.() ?? info.row.original.completionPercentage;
  return <ProgressBar value={value} />;
};

const ScoringReport = () => {
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationTitle, setNotificationTitle] = useState('');
  const [selectedCommittee, setSelectedCommittee] = useState(null);
  const [useFilteringTable, setUseFilteringTable] = useState(true);
  const [scoringTableData, setScoringTableData] = useState([]);
  const [scoringTableColumns, setScoringTableColumns] = useState([]);

  const {
    sectionalCommitteesArray,
    fellowsArray,
    nonAasRefereesArray,
    electionTypes,
    isLoading: staticLoading,
  } = useStaticCollections();

  const {
    candidateArray,
    committeeArray,
    candidateScoresArray,
    nominationArray,
    coiDeclarationArray,
    biasTrainingArray,
    isLoading: adminLoading,
  } = useAdminCollections();

  const { userDetails } = useLoggedInUserContextProvider();

  const { isAdmin, isSCObserver, hasAccess, userCommittee, userCommittees } =
    useUserPermissions(userDetails, committeeArray);

  const sectionalCommitteesWithCmse = useMemo(
    () =>
      addCmseCommitteeToSectionalCommitteeArray(sectionalCommitteesArray, true),
    [sectionalCommitteesArray],
  );

  useEffect(() => {
    if (!hasAccess) {
      setNotificationMessage(
        'You do not have permission to view scoring reports.',
      );
      setNotificationTitle('Access Denied');
    }
  }, [hasAccess]);

  useEffect(() => {
    if (!isAdmin && !isSCObserver && userCommittee && !selectedCommittee) {
      setSelectedCommittee(userCommittee.id);
    }
  }, [isAdmin, isSCObserver, userCommittee, selectedCommittee]);

  useEffect(() => {
    if (candidateScoresArray.length >= 0 && committeeArray.length > 0) {
      const { allTableData } = getScoringTableData(
        selectedCommittee,
        candidateScoresArray,
        committeeArray,
        sectionalCommitteesWithCmse,
        fellowsArray,
        nonAasRefereesArray,
        nominationArray,
        candidateArray,
        coiDeclarationArray,
        biasTrainingArray,
        electionTypes,
      );

      let filteredTableData = allTableData;

      if (isSCObserver && userCommittees.length > 0 && !selectedCommittee) {
        const observerCommitteeIds = userCommittees.map((c) => c.id);
        filteredTableData = filterObserverCommittees(
          allTableData,
          observerCommitteeIds,
        );
      }

      const tableColumns = filteringTableGetTableColumns(
        tableColumnNamesScoring,
        ColumnFilter,
      );

      const customColumns = tableColumns.map((column) => {
        if (column.id === 'completionStatus') {
          return {
            ...column,
            cell: renderCompletionStatus,
          };
        }
        if (column.id === 'completionPercentage') {
          return {
            ...column,
            cell: renderProgressBar,
          };
        }

        return column;
      });

      setScoringTableColumns(customColumns);
      setScoringTableData(filteredTableData);
    }
  }, [
    candidateScoresArray,
    committeeArray,
    selectedCommittee,
    sectionalCommitteesWithCmse,
    biasTrainingArray,
    isSCObserver,
    userCommittees,
  ]);

  const renderReportContent = () => {
    if (useFilteringTable) {
      return (
        <div className="mt-xl-5">
          <FilteringTable
            componentName="ScoringReport"
            columns={scoringTableColumns}
            data={scoringTableData}
            urlString=""
            buttonText=""
            isLoading={staticLoading || adminLoading}
          />
        </div>
      );
    }

    return (
      <div className="mt-xl-5">
        <p className="text-muted text-center">
          Please use the Filtering view to see the scoring completion data.
        </p>
      </div>
    );
  };

  if (!hasAccess) {
    return (
      <PageTitle
        motherMenu="Reports"
        activeMenu="Scoring Completion"
        notificationMessage={notificationMessage}
        notificationTitle={notificationTitle}
      />
    );
  }

  return (
    <>
      <PageTitle
        motherMenu="Reports"
        activeMenu="Scoring Completion"
        notificationMessage={notificationMessage}
        notificationTitle={notificationTitle}
      />

      {renderReportContent()}
    </>
  );
};

export default ScoringReport;
